import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { SETTING_KEYS, type SiteFacts as Facts, type Translatable } from '@hrlabs/shared-types';
import { siteFactsSchema } from '@hrlabs/validation';
import { getRow, saveRow } from '../../lib/db';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';
import { ArrayField } from '../../components/ArrayField';

const empty = (): Translatable => ({ en: '', bg: '' });

export function SiteFacts() {
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [founded, setFounded] = useState('');
  const [stats, setStats] = useState<Facts['stats']>([]);
  const [clients, setClients] = useState('');
  const [ratingOn, setRatingOn] = useState(false);
  const [rating, setRating] = useState({ value: '', count: '' });
  const [legal, setLegal] = useState({ registeredName: '', eik: '', vat: '', dataController: '' });

  useEffect(() => {
    getRow('settings', SETTING_KEYS.siteFacts).then((r) => {
      try {
        const v = JSON.parse(String(r?.value ?? '{}')) as Partial<Facts>;
        setFounded(v.founded ?? '');
        setStats(Array.isArray(v.stats) ? v.stats : []);
        setClients((v.clients ?? []).join('\n'));
        setRatingOn(!!v.rating);
        setRating({ value: v.rating?.value ?? '', count: v.rating?.count ?? '' });
        setLegal({ registeredName: '', eik: '', vat: '', dataController: '', ...(v.legal ?? {}) });
      } catch {
        /* start empty */
      }
      setLoading(false);
    });
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = siteFactsSchema.safeParse({
      founded: founded.trim(),
      stats,
      clients: clients.split('\n').map((c) => c.trim()).filter(Boolean),
      rating: ratingOn ? rating : null,
      legal,
    });
    if (!parsed.success) {
      notify('danger', 'Please check the values (max 6 stats, 16 clients, a rating needs a value).');
      return;
    }
    setSaving(true);
    try {
      await saveRow('settings', SETTING_KEYS.siteFacts, {
        value: JSON.stringify(parsed.data),
        type: 'json',
        group: 'site',
        description: 'Site-wide facts for the homepage/footer.',
      });
      notify('success', 'Site facts saved.');
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;
  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 820 }}>
      <p className="mb-1"><Link to="/settings">← Settings</Link></p>
      <h3 className="fw-bold mb-1">Site facts</h3>
      <p className="text-muted small mb-3">
        Shown on the homepage. Only enter figures that are true and that you can defend —
        wrong numbers are worse than none. Leave a list empty to hide that section.
      </p>

      <div className="mb-4" style={{ maxWidth: 200 }}>
        <label className="form-label fw-semibold">Founded (year)</label>
        <input className="form-control" value={founded} onChange={(e) => setFounded(e.target.value)} maxLength={10} />
      </div>

      <ArrayField label="Statistics band" items={stats} onChange={setStats} blank={() => ({ n: empty(), l: empty() })} addLabel="Add statistic"
        hint="e.g. 60+ — placements closed"
        render={(s, set) => (<>
          <BilingualField label="Figure" value={s.n} onChange={(v) => set({ ...s, n: v })} rows={1} />
          <BilingualField label="Label" value={s.l} onChange={(v) => set({ ...s, l: v })} rows={1} />
        </>)} />

      <div className="mb-4">
        <label className="form-label fw-semibold">Client names (one per line)</label>
        <textarea className="form-control" rows={8} value={clients} onChange={(e) => setClients(e.target.value)} />
        <div className="form-text">Shown in the &quot;trusted by&quot; strip. Only list clients that agreed to be named.</div>
      </div>

      <div className="mb-4">
        <div className="form-check mb-2">
          <input className="form-check-input" type="checkbox" id="ratingOn" checked={ratingOn} onChange={(e) => setRatingOn(e.target.checked)} />
          <label className="form-check-label fw-semibold" htmlFor="ratingOn">Show Google rating</label>
        </div>
        {ratingOn && (
          <div className="row g-2" style={{ maxWidth: 360 }}>
            <div className="col-6"><label className="form-label">Rating</label><input className="form-control" value={rating.value} onChange={(e) => setRating({ ...rating, value: e.target.value })} placeholder="4.9" /></div>
            <div className="col-6"><label className="form-label">Review count (optional)</label><input className="form-control" value={rating.count} onChange={(e) => setRating({ ...rating, count: e.target.value })} placeholder="38" /></div>
            <div className="form-text">Must match your real Google Business Profile.</div>
          </div>
        )}
      </div>

      <fieldset className="border rounded p-3 mb-4">
        <legend className="fs-6 fw-bold px-2 w-auto">Legal details (About page)</legend>
        <p className="small text-muted">Only fields you fill in are shown, exactly as typed. Use the values from the Commercial Register.</p>
        <div className="row g-3">
          <div className="col-md-6"><label className="form-label fw-semibold">Registered company name</label>
            <input className="form-control" value={legal.registeredName} onChange={(e) => setLegal({ ...legal, registeredName: e.target.value })} /></div>
          <div className="col-md-6"><label className="form-label fw-semibold">EIK (company ID)</label>
            <input className="form-control" value={legal.eik} onChange={(e) => setLegal({ ...legal, eik: e.target.value })} /></div>
          <div className="col-md-6"><label className="form-label fw-semibold">VAT number</label>
            <input className="form-control" value={legal.vat} onChange={(e) => setLegal({ ...legal, vat: e.target.value })} /></div>
          <div className="col-md-6"><label className="form-label fw-semibold">Data controller registration</label>
            <input className="form-control" value={legal.dataController} onChange={(e) => setLegal({ ...legal, dataController: e.target.value })} /></div>
        </div>
      </fieldset>

      <button className="btn btn-success" disabled={saving} type="submit">{saving ? 'Saving…' : 'Save'}</button>
    </form>
  );
}
